import { Vacancy } from 'types/vacancy'
import { ShareVacancy } from './share-vacancy'
import { clearTextRichToHTML } from 'utils/format'
import { Box, Icon, VacancyBadges } from 'components'

const languagesList = [
  '',
  'Iniciante',
  'Básico',
  'Intermediário',
  'Avançado',
  'Fluente',
  'Domínio'
]

const skillsList = ['', 'Básico', 'Intermediário', 'Avançado']

interface Props extends Vacancy {
  isDisabled: boolean
}

export const VacancyContent = (vacancy: Props) => {
  const requirements = vacancy.hardSkills
    .concat(vacancy.language)
    .concat(vacancy.education)

  return (
    <section className="w-full max-w-7xl self-center flex flex-col gap-4 mt-3 px-4 xl:px-0 pb-10">
      {!vacancy.isDisabled && <ShareVacancy />}
      <Box className="flex flex-col gap-y-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-gray-900">
          <p className="flex items-center gap-x-1.5 text-gray-900 text-caption">
            <Icon face="location_on" />
            {vacancy.address.short}
          </p>

          <p className="flex items-center gap-x-2 text-caption">
            <Icon face="business_center" />
            {vacancy.availablePositions === 1
              ? '1 posição'
              : `${vacancy.availablePositions} posições`}
          </p>

          {vacancy.salary && (
            <p className="flex items-center gap-x-2 text-caption">
              <Icon face="monetization_on" />
              {vacancy.salary}
            </p>
          )}

          {(vacancy.pcdOnly || vacancy.peopleWithDisabilities) && (
            <p className="flex items-center gap-x-2 text-caption">
              <Icon face="accessibility_new" />
              {vacancy.pcdOnly ? 'Exclusivo' : 'Elegível'} para PCD
            </p>
          )}

          {vacancy.shift && (
            <p className="flex items-center gap-x-2 text-caption">
              <Icon face="access_alarms" />
              {vacancy.shift}
            </p>
          )}
        </div>

        <VacancyBadges vacancy={vacancy} />

        <span
          className="break-words whitespace-pre-wrap vacancy-description"
          dangerouslySetInnerHTML={{
            __html: clearTextRichToHTML(vacancy.description)
          }}
        />
      </Box>
      {!!requirements.length && (
        <Box className="flex flex-col gap-y-4">
          <h2 className="text-xs font-semibold mb-1">Requisitos</h2>

          {!!vacancy.education.length && (
            <div>
              <h3 className="font-subtitle1 font-semibold">Escolaridade</h3>
              <ul
                data-cy="vacancy-educations"
                className="list-disc pl-6 break-words"
              >
                {vacancy?.education.map((requirement, index) => (
                  <li key={requirement.id + index} className="leading-7">
                    {requirement.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!vacancy.language.length && (
            <div>
              <h3 className="font-subtitle1 font-semibold">Idioma</h3>
              <ul
                data-cy="vacancy-languages"
                className="list-disc pl-6 break-words"
              >
                {vacancy?.language.map((requirement, index) => (
                  <li key={requirement.id + index} className="leading-7">
                    {requirement.name} -{' '}
                    <span className="text-gray-700">
                      {languagesList[requirement.level]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!vacancy.hardSkills.length && (
            <div>
              <h3 className="font-subtitle1 font-semibold">
                Habilidade Técnica
              </h3>
              <ul
                data-cy="vacancy-hard-skills"
                className="list-disc pl-6 break-words"
              >
                {vacancy?.hardSkills.map((requirement, index) => (
                  <li key={requirement.id + index} className="leading-7">
                    {requirement.name}
                    {vacancy.showSkillsLevel && (
                      <>
                        {' - '}
                        <span className="text-gray-700">
                          {skillsList[requirement.level]}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Box>
      )}
      {!!vacancy.benefits.length && (
        <Box className="flex flex-col gap-y-4">
          <h2 className="text-xs font-semibold mb-1">Benefícios</h2>

          <ul className="list-disc pl-6 break-words">
            {vacancy?.benefits.map((benefit, index) => (
              <li key={benefit.id + index} className="leading-7">
                {benefit.name}
              </li>
            ))}
          </ul>
        </Box>
      )}
      <Box className="flex flex-col gap-y-4">
        <h2 className="text-xs font-semibold mb-1">Localização</h2>

        <div className="flex items-center gap-x-2 text-gray-600 break-words">
          <Icon face="location_on" />
          <span>{vacancy.address.long}</span>
        </div>
      </Box>
    </section>
  )
}
